import { datadogRum } from "@datadog/browser-rum";

// Type declaration for Blob to avoid error message
export interface Blob {
  arrayBuffer: () => Promise<ArrayBuffer>;
}

// Server expects response within 60s (10s grace period for latency etc)
export const WEBSOCKET_KEEP_ALIVE = 50000; // 50s
export const PING = 57; // Uint8Array([57])
export const PONG = new Uint8Array([65]);

/**
 * Validates if incoming data is PING
 * @param data Data blob
 * @returns Boolean - Validation result
 */
export const isPing = async (data: any) => {
  if (data) {
    // clearly something wrong under-the-hood, "globalThis" is needed to get the correct type instance of Blob
    const blob: globalThis.Blob = new Blob([data]); // Blob form data
    let buffer: ArrayBuffer;
    let view: Uint8Array;

    // check if we're using a new browser or old one, e.g. if the arrayBuffer() function exists
    if (typeof blob.arrayBuffer == "function") {
      buffer = await blob.arrayBuffer(); // Arraybuffer from blob (not supported on older browsers and will throw a blocking error)
      view = new Uint8Array(buffer); // TypedArray from buffer
      return view.length === 1 && view[0] == PING;
    } else {
      // older browsers don't have Blob.arrayBuffer(), so instead use a filereader to get the Uint8Array again
      var fileReader = new FileReader();
      fileReader.onload = function (event) {
        if (event.target && event.target.result) {
          buffer = event.target.result as ArrayBuffer;
          view = new Uint8Array(buffer);
        } else return false;
        return view.length === 1 && view[0] == PING;
      };
      fileReader.readAsArrayBuffer(blob);
    }
  } else {
    return false;
  }
};

/** Websocket message type */
export const enum WsMessageType {
  WS_SERVER = "ws_server",
}

/** Current connection status */
export const enum WsStatus {
  CONNECTED = "connected",
  REJECT = "reject",
  DISCONNECT = "disconnect",
  SUBSCRIBED = "subscribed",
  UNSUBSCRIBED = "unsubscribed",
  REDIS_CONNECTION_DISCONNECT = "redis_connection_disconnect",
}

/** Reason for the success or error event (e.g. connection to be terminated) */
export const enum WsReason {
  // Error
  UNDEFINED_ERROR = "undefined_error",
  INACTIVE = "inactive",
  MISSING_TOKEN = "missing_token",
  INVALID_TOKEN = "invalid_token",
  JSON_PARSE_ERROR = "json_parse_error",
  SUBSCRIBE_ERROR = "subscribe_error",
  UNSUBSCRIBE_ERROR = "unsubscribe_error",
  PUBLISH_ERROR = "publish_error",
  REDIS_ERROR = "redis_error",
  // Success
  CONNECT_SUCCESS = "connect_success",
  SUBSCRIBE_SUCCESS = "subscribe_success",
  UNSUBSCRIBE_SUCCESS = "unsubscribe_success",
  AUTH_SUCCESS = "auth_success",
}

/** Ws message object */
export interface WsMessage {
  type: WsMessageType;
  /** Current connection status */
  status: WsStatus;
  /** Connection identifier */
  id: string;
  /** Reason for the error message (e.g. 'json_parse_error') */
  reason?: WsReason;
  /** General JavaScript error */
  error: string | null;
  /** User identifier if given */
  userId?: string;
  /** Subscription name if given (e.g. 'conversation/UUID') */
  subscribeName?: string;
}

/**
 * Parses a system message comming from Kotozna Websocket server
 * @param data Any data
 * @returns A Websocket system message or null
 */
export const parseWsSystemMessage = (data: any): WsMessage | null => {
  try {
    let parsedMessage = JSON.parse(data) as WsMessage;
    if (parsedMessage.type == WsMessageType.WS_SERVER) {
      return parsedMessage;
    } else {
      return parsedMessage;
    }
  } catch {
    return null;
  }
};

/**
 * Logs a possible error to DataDog
 * @param message Websocket system message
 * @param userAccessId Current user access id
 */
export const logToDataDogIfWsErrorMessage = (message: WsMessage | null, userAccessId?: string): void => {
  if (message == null) {
    return;
  }
  // Check if message is error message
  if (message && message.status && (message.status == WsStatus.REJECT || message.status == WsStatus.DISCONNECT)) {
    try {
      const reason = message.reason || WsReason.UNDEFINED_ERROR;
      const userId = message.userId || userAccessId;
      new WsError(reason, message.status, message.id, userId, message.subscribeName, message.error);
    } catch (error) {
      //formerly Sentry.captureException(error);
      datadogRum.addError(error);
      console.error(error);
    }
  }
};

/**
 * Custom websocket server error
 */
export class WsError extends Error {
  /** Reason for the error */
  reason: WsReason;
  /** Current connection status */
  status: WsStatus;
  /** Connection identifier */
  id: string;
  /** (Optional) User identifier */
  userId?: string;
  /** (Optional) Subscription name */
  subscribeName?: string;

  constructor(
    reason: WsReason,
    status: WsStatus,
    id: string,
    userId?: string,
    subscribeName?: string,
    ...params: any[]
  ) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(...params);

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, WsError);
    }

    this.name = "WsError";
    // Custom debugging information
    this.reason = reason;
    this.status = status;
    this.id = id;
    this.userId = userId;
    this.subscribeName = subscribeName;
  }
}
